import { useEffect, useRef } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import useDragRace from '../../application/compare/useDragRace';
import useStartLights, { LIGHT_STAGES } from '../../application/motion/useStartLights';
import { carName } from '../../domain/cars/carCatalogue';
import { SERIES_CLASS } from './comparePlot';
import DragRaceConditions from './DragRaceConditions';
import DragRaceResult from './DragRaceResult';
import DragRaceStrip from './DragRaceStrip';
import RaceBriefing from './RaceBriefing';
import RaceChart from './RaceChart';
import { CloseIcon } from '../icons/ControlIcons';

/**
 * The duel, full screen (SPEC-008 PR-8.12).
 *
 * Everything about the race that used to be a band on a scrolling page is here
 * instead, in a dialog that fills the device: the road, the two cars briefed
 * against each other, the strip, its chart, a starting tree and the verdict.
 *
 * IT IS A PLACE YOU GO, not a section you scroll past, and that is the whole
 * decision. A race that shares a page with a specification table is a chart
 * among charts — the reader arrives at it mid-scroll, watches twelve seconds of
 * it with a spec table in the corner of their eye, and leaves. Taken full
 * screen it can do what it actually is: two cars, a set of lights and a result,
 * with nothing else on the glass.
 *
 * A real `<dialog>` opened with `showModal()`, exactly as `CarSelectSheet` and
 * `GarageFilterSheet` are. Focus trapping, Escape, the top layer and an inert
 * background are four things the platform gets right; what is drawn here is the
 * stage and never the modal behaviour.
 *
 * The RACE ITSELF is unchanged — same physics, same clock, same freeze moments,
 * same chart. What changed is where the reader watches it from.
 */

/** The tree's lamps, top to bottom, and which stage lights each. */
const LAMPS = [
    { key: 'set', className: 'race-lights__lamp--amber' },
    { key: 'ready', className: 'race-lights__lamp--amber' },
    { key: 'go', className: 'race-lights__lamp--green' },
];

/**
 * The starting tree.
 *
 * Lit CUMULATIVELY — an amber that goes out when the next one lights is a tree
 * counting down to nothing, where a real one fills. `aria-hidden` because the
 * button beside it already says what is happening and a live region announcing
 * three lamps in a second and a half is noise.
 */
function RaceLights({ stage }) {
    const t = useTranslate();
    const reached = LIGHT_STAGES.indexOf(stage);

    return (
        <p className="race-lights" aria-hidden="true" title={t('compare.stage.lights')}>
            {LAMPS.map((lamp, index) => (
                <span
                    key={lamp.key}
                    className={[
                        'race-lights__lamp',
                        lamp.className,
                        reached > index ? 'race-lights__lamp--lit' : '',
                    ].filter(Boolean).join(' ')}
                />
            ))}
        </p>
    );
}

export default function RaceStage({ cars, conditions, onClose, onCondition }) {
    const t = useTranslate();
    const dialogRef = useRef(null);
    const {
        gap,
        markers,
        moment,
        positions,
        race,
        rate,
        reducedMotion,
        run,
        skip,
        status,
    } = useDragRace({ cars, conditions });
    // Destructured, because the hook returns a fresh object every render and the
    // effect below depends on one of its members.
    const { reset: resetLights, stage: lightStage, start: startLights } = useStartLights(
        run,
        { reducedMotion },
    );

    // Opening is a one-off, and deliberately its own effect: an effect that
    // depended on `onClose` would close and re-open the dialog whenever the
    // parent re-rendered, and the second `showModal()` throws.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.showModal();

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.addEventListener('close', onClose);

        return () => dialog.removeEventListener('close', onClose);
    }, [onClose]);

    /**
     * A new road is a new start, so the tree goes dark with the race it belonged
     * to rather than staying green over one nobody has run.
     *
     * Keyed on the RACE and on nothing else. It was keyed on the clock's status
     * being `idle`, which is true during the tree as well — the cars have not
     * moved yet — so every render while the lights were counting reset the
     * lights, cancelled the chain and left the strip on a green that never
     * fired. `reset` is a stable callback; the hook's object around it is not,
     * which is why it is destructured above.
     */
    useEffect(() => {
        resetLights();
    }, [race, resetLights]);

    const isStaging = lightStage !== 'idle' && status === 'idle';
    const isRunning = status === 'running';
    const isFinished = status === 'finished';

    return (
        <dialog className="race-stage" ref={dialogRef} aria-labelledby="race-stage-title">
            <header className="race-stage__head">
                <h2 className="race-stage__title" id="race-stage-title">
                    {t('compare.stage.title', { a: carName(cars[0]), b: carName(cars[1]) })}
                </h2>
                <button
                    type="button"
                    className="round-button"
                    onClick={() => dialogRef.current.close()}
                    aria-label={t('compare.stage.close')}
                >
                    <CloseIcon size={20} />
                </button>
            </header>

            {race ? (
                <div className="race-stage__body">
                    <DragRaceConditions conditions={conditions} onChange={onCondition} />

                    {/* The briefing is an INTRODUCTION and it clears when the
                        lights do. It used to stay through the run, on the
                        argument that it is context for what is being watched;
                        that argument held while this was a band on a scrolling
                        page and fails on a fixed stage, where the two hundred
                        pixels it holds are the two hundred the chart needs. You
                        cannot give context to something the reader cannot see.

                        Clearing it with the TREE rather than with the flag means
                        the one layout shift lands on the screen getting ready
                        rather than under a car already moving. */}
                    {status === 'idle' && lightStage === 'idle'
                        ? <RaceBriefing cars={cars} conditions={conditions} />
                        : null}

                    <DragRaceStrip
                        cars={cars}
                        gap={gap}
                        moment={moment}
                        positions={positions}
                        race={race}
                        revealed={isFinished}
                    />

                    <RaceChart
                        ceiling={Math.max(...race.lanes.map((lane) => lane.topKmh))}
                        distance={race.distance}
                        lanes={race.lanes}
                        markers={markers}
                        positions={positions}
                        seriesClasses={SERIES_CLASS}
                    />

                    {isFinished ? <DragRaceResult cars={cars} race={race} /> : null}
                </div>
            ) : (
                <p className="compare__note">{t('compare.race.unavailable')}</p>
            )}

            {race ? (
                <div className="race-stage__foot">
                    <RaceLights stage={lightStage} />

                    <button
                        type="button"
                        className="button button--primary race-stage__run"
                        onClick={isRunning ? skip : startLights}
                        // The tree is running and the cars have not moved: a
                        // second tap here would start a second sequence over the
                        // first. `aria-disabled` rather than `disabled`, which
                        // drops focus on the spot in several engines.
                        aria-disabled={isStaging || undefined}
                    >
                        {(() => {
                            if (isRunning) {
                                return t('compare.race.skip');
                            }

                            return t(isFinished ? 'compare.race.again' : 'compare.race.run');
                        })()}
                    </button>

                    {isRunning && rate > 1 && !reducedMotion ? (
                        <p className="compare__rate">{t('compare.race.rate', { rate: Math.round(rate) })}</p>
                    ) : null}
                </div>
            ) : null}
        </dialog>
    );
}
